/* Small display widgets: stars, progress bar, ring, tabs, level meter, slots. */
import { type ReactNode, type Ref, useEffect, useImperativeHandle, useRef } from 'react';
import { getMicLevel, useMicFrame } from '../core/mic';
import { clamp, levelPct } from '../core/util';
import { Icon, type IconName } from './icons';

export function Stars({ n, max = 3 }: { n: number; max?: number }) {
  return (
    <span className="stars" role="img" aria-label={`${n} / ${max} yıldız`}>
      {Array.from({ length: max }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length list
        <Icon key={i} name="star" className={i < n ? 'on' : 'off'} />
      ))}
    </span>
  );
}

export function ProgressBar({ frac }: { frac: number }) {
  return (
    <span className="pbar">
      <span className="pbar-fill" style={{ width: `${(clamp(frac, 0, 1) * 100).toFixed(1)}%` }} />
    </span>
  );
}

export interface RingHandle {
  set(frac: number): void;
}

/** Circular progress; `ref.set()` updates it without re-rendering. */
export function Ring({
  frac,
  size = 44,
  label,
  ref,
}: {
  frac: number;
  size?: number;
  label?: ReactNode;
  ref?: Ref<RingHandle>;
}) {
  const fg = useRef<SVGCircleElement>(null);
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  useImperativeHandle(ref, () => ({
    set(f) {
      fg.current?.setAttribute('stroke-dashoffset', String(c * (1 - clamp(f, 0, 1))));
    },
  }));
  return (
    <span className="ring-wrap">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="ring" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} className="ring-bg" />
        <circle
          ref={fg}
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="ring-fg"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamp(frac, 0, 1))}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {label != null ? <span className="ring-label">{label}</span> : null}
    </span>
  );
}

export interface TabItem<T extends string> {
  id: T;
  label: string;
  icon?: IconName;
  badge?: string;
}

export function Tabs<T extends string>({
  items,
  active,
  onChange,
}: {
  items: readonly TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  // the open tab is always in view, also when the row is wider than a phone
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the open tab changes
  useEffect(() => {
    const el = list.current;
    const on = el?.querySelector<HTMLElement>('.tab.on');
    if (!el || !on) return;
    const a = el.getBoundingClientRect();
    const b = on.getBoundingClientRect();
    el.scrollLeft += b.left - a.left - (a.width - b.width) / 2;
  }, [active]);
  return (
    <div className="tabs" role="tablist" ref={list}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          role="tab"
          className={`tab${it.id === active ? ' on' : ''}`}
          aria-selected={it.id === active}
          onClick={() => onChange(it.id)}
        >
          {it.icon ? <Icon name={it.icon} /> : null}
          <span>{it.label}</span>
          {it.badge ? <span className="tab-badge">{it.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Live microphone level with the detection threshold marker. */
export function MicLevelMeter() {
  const el = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const gate = useRef<HTMLDivElement>(null);
  useMicFrame((f) => {
    const level = getMicLevel();
    const pct = levelPct(level);
    if (bar.current) bar.current.style.width = `${pct}%`;
    el.current?.setAttribute('aria-valuenow', String(Math.round(pct)));
    if (gate.current) gate.current.style.left = `${levelPct(f.gate)}%`;
    el.current?.classList.toggle('hot', level > f.gate);
  });
  return (
    // biome-ignore lint/a11y/useSemanticElements: styled bar updated at frame rate; <meter> cannot show the gate marker
    <div
      className="level-meter"
      role="meter"
      aria-label="Mikrofon seviyesi"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={0}
      ref={el}
    >
      <div className="lm-bar" ref={bar} />
      <div className="lm-gate" ref={gate} />
    </div>
  );
}

export interface SlotView {
  main: string;
  sub: string;
  /** ok | bad | reveal | wrong, space-separated */
  cls: string;
  /** bump to replay the shake animation */
  shake: number;
}

export const emptySlots = (n: number): SlotView[] =>
  Array.from({ length: n }, () => ({ main: '', sub: '', cls: '', shake: 0 }));

/** Answer slots for multi-note questions. */
export function Slots({ slots, current }: { slots: readonly SlotView[]; current: number }) {
  return (
    <div className="slots">
      {slots.map((sl, i) => (
        <div
          // Remounting on shake restarts the CSS animation.
          // biome-ignore lint/suspicious/noArrayIndexKey: slots are positional
          key={`${i}.${sl.shake}`}
          className={`slot${i === current ? ' cur' : ''}${sl.cls ? ` ${sl.cls}` : ''}${sl.shake ? ' shake' : ''}`}
        >
          <span className="slot-main">{sl.main}</span>
          <span className="slot-sub">{sl.sub}</span>
        </div>
      ))}
    </div>
  );
}
